import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { BoardBed, BoardOccupant, BoardWard } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  TextArea,
  formatShortAge,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { bedBoardQuery, setBedStatus } from '../api/inpatient.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import styles from './inpatient.module.css';

// Wards: the bed board (docs/plans/phase2-inpatient-adt.md §6; DESIGN.md §8). Each ward's
// beds as labelled tiles; the state is always a word, the shape tells states apart.

const STATE_WORDS: Record<BoardBed['state'], string> = {
  occupied: 'Occupied',
  free: 'Free',
  cleaning: 'Cleaning',
  blocked: 'Blocked',
  retired: 'Retired',
};

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function occupantLine(o: BoardOccupant): string {
  if (o.patient === null) return 'Restricted record';
  return `${o.patient.displayName}, ${o.patient.sex} ${formatShortAge(o.patient.age, false)}`;
}

export function daysText(days: number): string {
  return days === 1 ? 'Day 1' : `Day ${String(days)}`;
}

function BedTile({ bed, onChoose }: { bed: BoardBed; onChoose: (bed: BoardBed) => void }) {
  const navigate = useNavigate();
  const occupant = bed.occupant;
  // Precautions in place (operations plan §2.1): passive words on the tile, never blocking.
  const precautions = occupant?.precautions ?? [];
  // What a bed waiting for cleaning waits for (housekeeping plan §2.1): never a patient.
  const cleaning = bed.cleaning ?? null;
  const label = `Bed ${bed.label}, ${STATE_WORDS[bed.state]}${
    cleaning === null ? '' : `, ${cleaning.words}`
  }${occupant === null ? '' : `, ${occupantLine(occupant)}`}${
    precautions.length === 0 ? '' : `, ${precautions.join(', ')}`
  }`;
  return (
    <li>
      <button
        type="button"
        className={styles.bed}
        data-state={bed.state}
        aria-label={label}
        disabled={occupant !== null && occupant.patient === null}
        onClick={() => {
          if (occupant === null) onChoose(bed);
          else
            void navigate({
              to: '/admissions/$admissionId',
              params: { admissionId: occupant.admissionId },
            });
        }}
      >
        <span className={styles.bedLabel}>{bed.label}</span>
        <span className={styles.bedState}>
          {STATE_WORDS[bed.state]}
          {bed.roomName === null ? '' : `, ${bed.roomName}`}
        </span>
        {occupant === null ? null : (
          <>
            <span className={styles.bedName}>{occupantLine(occupant)}</span>
            <span className={styles.bedDays}>{daysText(occupant.daysAdmitted)}</span>
            {precautions.map((words) => (
              <span key={words} className={styles.precaution}>
                {words}
              </span>
            ))}
          </>
        )}
        {bed.state === 'blocked' && bed.statusReason !== null ? (
          <span className={styles.bedDays}>{bed.statusReason}</span>
        ) : null}
        {cleaning === null ? null : (
          <span className={styles.bedDays}>
            {cleaning.words.charAt(0).toUpperCase() + cleaning.words.slice(1)}
          </span>
        )}
      </button>
    </li>
  );
}

function WardCard({
  ward,
  onChoose,
  showWorklist,
}: {
  ward: BoardWard;
  onChoose: (bed: BoardBed) => void;
  /** The ward worklist and handover (ward rounds and nursing plan §5) need clinical.read. */
  showWorklist: boolean;
}) {
  const c = ward.counts;
  return (
    <Section
      title={ward.name}
      description={`${String(c.occupied)} occupied, ${String(c.free)} free, ${String(c.cleaning)} cleaning, ${String(c.blocked)} blocked`}
      {...(showWorklist
        ? {
            actions: (
              <Link
                to="/wards/$wardId"
                params={{ wardId: ward.id }}
                aria-label={`Worklist and handover for ${ward.name}`}
              >
                Worklist and handover
              </Link>
            ),
          }
        : {})}
    >
      {ward.beds.length === 0 ? (
        <p className={styles.meta}>No beds in this ward yet. Add them in Settings.</p>
      ) : (
        <ul className={styles.grid} aria-label={`Beds in ${ward.name}`}>
          {ward.beds.map((bed) => (
            <BedTile key={bed.id} bed={bed} onChoose={onChoose} />
          ))}
        </ul>
      )}
      {ward.waiting.length === 0 ? null : (
        <div>
          <h3 className={styles.bedLabel}>Waiting for a bed</h3>
          <ul className={styles.waiting} aria-label={`Waiting for a bed in ${ward.name}`}>
            {ward.waiting.map((o) => (
              <li key={o.admissionId} className={styles.waitingRow}>
                <span>{occupantLine(o)}</span>
                <span className={styles.bedDays}>{daysText(o.daysAdmitted)}</span>
                {o.patient === null ? null : (
                  <Link to="/admissions/$admissionId" params={{ admissionId: o.admissionId }}>
                    Give a bed
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

/** What can be done with an empty bed from the board. */
function BedDialog({ bed, onClose }: { bed: BoardBed; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [blocking, setBlocking] = useState(false);
  const [reason, setReason] = useState('');
  const change = useMutation({
    mutationFn: (body: {
      status: 'available' | 'cleaning' | 'blocked';
      reason?: string;
      terminal?: boolean;
    }) => setBedStatus(bed.id, body),
    onSuccess: async (_result, body) => {
      await queryClient.invalidateQueries({ queryKey: bedBoardQuery.queryKey });
      toasts.show({
        title:
          body.status === 'available'
            ? 'Bed marked clean'
            : body.status === 'blocked'
              ? 'Bed blocked'
              : body.terminal === true
                ? 'Bed sent for a terminal clean'
                : 'Bed sent for cleaning',
        description: `Bed ${bed.label}`,
      });
      onClose();
    },
  });
  return (
    <Dialog
      title={`Bed ${bed.label}: ${STATE_WORDS[bed.state]}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {blocking ? (
        <TextArea
          label="Why is the bed blocked?"
          value={reason}
          onChange={setReason}
          autoFocus
          description="At least 5 characters, e.g. Broken frame."
        />
      ) : (
        <p className={styles.meta}>
          {bed.state === 'free'
            ? 'This bed is free. Admit a patient to it from their visit, or take it out of use.'
            : bed.state === 'cleaning'
              ? `This bed is waiting to be cleaned${bed.cleaning == null ? '' : `: ${bed.cleaning.words}`}. Housekeeping marks it clean; you can too, when it is ready for the next patient.`
              : `This bed is blocked${bed.statusReason === null ? '' : `: ${bed.statusReason}`}.`}
        </p>
      )}
      {change.error === null ? null : (
        <Notice tone="error">
          {errorText(change.error, "The bed wasn't changed. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Close
        </Button>
        {blocking ? (
          <Button
            variant="primary"
            isDisabled={!online || reason.trim().length < 5}
            isPending={change.isPending}
            pendingLabel="Blocking…"
            onPress={() => {
              change.mutate({ status: 'blocked', reason: reason.trim() });
            }}
          >
            Block bed
          </Button>
        ) : (
          <>
            {bed.state === 'free' ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  change.mutate({ status: 'cleaning' });
                }}
              >
                Send for cleaning
              </Button>
            ) : null}
            {bed.state === 'free' ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  change.mutate({ status: 'cleaning', terminal: true });
                }}
              >
                Send for terminal clean
              </Button>
            ) : null}
            {bed.state !== 'blocked' ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setBlocking(true);
                }}
              >
                Block bed
              </Button>
            ) : null}
            {bed.state === 'cleaning' || bed.state === 'blocked' ? (
              <Button
                variant="primary"
                isDisabled={!online}
                isPending={change.isPending}
                pendingLabel="Saving…"
                onPress={() => {
                  change.mutate({ status: 'available' });
                }}
              >
                {bed.state === 'cleaning' ? 'Mark clean' : 'Unblock bed'}
              </Button>
            ) : null}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function BedBoardPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const board = useQuery(bedBoardQuery);
  const online = useOnline();
  const [wardFilter, setWardFilter] = useState<string>('all');
  const [chosen, setChosen] = useState<BoardBed | null>(null);
  const mayManage = session !== null && can(session, 'inpatient.beds.manage');

  const wards = board.data?.wards ?? [];
  const shown = wardFilter === 'all' ? wards : wards.filter((w) => w.id === wardFilter);
  return (
    <div className={styles.page}>
      <PageHeader
        title="Wards"
        description="Who is in which bed, which beds are free, and who is waiting for one."
        {...(wards.length > 1
          ? {
              actions: (
                <Select
                  label="Ward"
                  dense
                  options={[
                    { id: 'all', label: 'All wards' },
                    ...wards.map((w) => ({ id: w.id, label: w.name })),
                  ]}
                  value={wardFilter}
                  onChange={(v) => {
                    setWardFilter(String(v));
                  }}
                />
              ),
            }
          : {})}
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. The board shows what was loaded last and can't change beds until
          the connection is back.
        </Notice>
      ) : null}
      {board.isPending ? <LoadingBlock label="Loading the bed board" rows={6} /> : null}
      {board.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void board.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(board.error, "The bed board couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {board.isSuccess && wards.length === 0 ? (
        <EmptyState>
          No wards set up yet. An administrator adds wards and beds in Settings, Wards and beds.
        </EmptyState>
      ) : null}
      {shown.map((ward) => (
        <WardCard
          key={ward.id}
          ward={ward}
          onChoose={mayManage ? setChosen : () => undefined}
          showWorklist={
            session !== null && can(session, 'clinical.read') && can(session, 'inpatient.read')
          }
        />
      ))}
      {chosen === null ? null : (
        <BedDialog
          bed={chosen}
          onClose={() => {
            setChosen(null);
          }}
        />
      )}
    </div>
  );
}
